import { Fragment, useEffect, useState, useCallback } from "react";
import { User } from "../../models/User";
import { Group } from "../../models/Group";
import { isEmpty, first, map, replace } from 'lodash'
import { Organization } from "../../models/Organization";
import { NoContentWrapper, getTrendClass, renderPeriod } from "../../helpers/renderHelper";
import { Dropdown } from "react-bootstrap";
import { useLazyQuery } from "@apollo/client";
import { GET_USERS, GET_GROUPS } from "../../graphQL/queries";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setGroup, setUsers } from "../../redux/appStateSlice";
import DriverCard from "../common/DriverCard";
import LoadingSpinner from "../common/LoadingSpinner";
import { Paths } from "../routes/Routes";
import { useNavigate } from "react-router";
import { buildGroupDrivers, buildGroupTopStreaks } from '../../helpers/managerHelper';
import { clearBackNavigation, setSelectedGroup } from "../../redux/navigationSlice";
import { AppMessages } from '../../strings';
import ManagerTopStreaksPanel from "./ManagerTopStreaksPanel";

const ManagerSummary = ({currentUser, currentOrganization, allGroups}:{currentUser?: User, currentOrganization?:Organization, allGroups?:Group[]}) => {

	const dispatch = useAppDispatch();
	const navigate = useNavigate()

	const userGroups = useAppSelector(state => state.appState.groupUsers)
	const currentSelectedGroup = useAppSelector(state => state.nav.currentSelectedGroup)
	const [hasFetchedGroup, setHasFetchedGroup] = useState(false)
	const [hasFetchedDrivers, setHasFetchedDrivers] = useState(false)
	const [groupSearchText, setGroupSearchText] = useState("")

	const [getDrivers, getDriversResponses ] = useLazyQuery(GET_USERS);

	const [getGroup, getGroupResponses ] = useLazyQuery(GET_GROUPS);
		
	const allDrivers = currentSelectedGroup ? userGroups[currentSelectedGroup.groupID]?.users : []
	const drivers = buildGroupDrivers(allDrivers);
	const topStreaks = buildGroupTopStreaks(currentOrganization?.streaks, allDrivers);
	const hasData = hasFetchedGroup && hasFetchedDrivers
	const groupScorecard = first(currentSelectedGroup?.scorecard)
	const hasGroupScore = !!groupScorecard?.avgCurrentScore
	const normalizedGroupSearch = groupSearchText.trim().toLowerCase()
	const filteredGroups = (allGroups ?? []).filter((group) => {
		if (!normalizedGroupSearch) {
			return true
		}
		return (group.name ?? "").toLowerCase().includes(normalizedGroupSearch)
	})

	const setCurrentSelectedGroup = useCallback((group: Group) => {

		setHasFetchedGroup(false);
		setHasFetchedDrivers(false);

		dispatch(setSelectedGroup(group))
	}, [dispatch])

	useEffect(()=>{
		dispatch(clearBackNavigation())
	},[dispatch])

	// Call build
	useEffect(()=>{
		if (currentUser && allGroups) {
			if (!isEmpty(allGroups) && !currentSelectedGroup) {
				setCurrentSelectedGroup(first(allGroups));
			}
		}
	}, [currentUser, allGroups, currentSelectedGroup, setCurrentSelectedGroup]);

	useEffect(()=>{
		if (currentSelectedGroup && currentUser) {
			getGroup({
				variables: {
					groupID: currentSelectedGroup?.groupID,
					isLocationGroup: true,
					hasDrivers: true,
					startDate: import.meta.env.REACT_APP_MANAGER_START_DATE ? import.meta.env.REACT_APP_MANAGER_START_DATE : null,
					endDate: import.meta.env.REACT_APP_MANAGER_END_DATE ? import.meta.env.REACT_APP_MANAGER_END_DATE : null,
					prevStartDate: import.meta.env.REACT_APP_MANAGER_PREV_START_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_START_DATE : null,
					prevEndDate: import.meta.env.REACT_APP_MANAGER_PREV_END_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_END_DATE : null
				}
			});
			if (currentSelectedGroup?.groupID !== -1 && currentUser?.isInDriverRole === false) {
				getDrivers({
					variables: {
						groupID: currentSelectedGroup?.groupID ?? -1,
						onlyDrivers: true,
						startDate: import.meta.env.REACT_APP_MANAGER_START_DATE ? import.meta.env.REACT_APP_MANAGER_START_DATE : null,
						endDate: import.meta.env.REACT_APP_MANAGER_END_DATE ? import.meta.env.REACT_APP_MANAGER_END_DATE : null,
						prevStartDate: import.meta.env.REACT_APP_MANAGER_PREV_START_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_START_DATE : null,
						prevEndDate: import.meta.env.REACT_APP_MANAGER_PREV_END_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_END_DATE : null
					}
				})
			}
		}
	}, [currentSelectedGroup, currentUser, getGroup, getDrivers])

	useEffect(()=>{
		if (getDriversResponses?.data) {
			dispatch(setUsers({groupId: currentSelectedGroup?.groupID ?? -1, groupUsers:getDriversResponses?.data}))

			setHasFetchedDrivers(true);
		}
	},[getDriversResponses?.data, currentSelectedGroup?.groupID, dispatch])

	useEffect(()=>{
		if (getGroupResponses?.data?.groups) {
			dispatch(setGroup(first(getGroupResponses?.data.groups)));

			setHasFetchedGroup(true);
		}
	},[getGroupResponses?.data, dispatch])

	function navigateToDetails(e, user) {
		e.stopPropagation();

		const path = replace(Paths.MANAGER_DRIVER_DETAIL, ':id', user?.userID).replace(':groupid', currentSelectedGroup?.groupID);
		navigate(path);
	}

	/**
	 * Renders the group average score card, or a compact no-score reason when unscored.
	 */
	function renderGroupScore() { 
		const scorecard = first(currentSelectedGroup?.scorecard)
		const trendClass = getTrendClass(scorecard?.trend, "")

		if (!scorecard?.avgCurrentScore) {
			return (
				<div className="row" >
					<div className="col-12">
						<div className="card" style={{padding: '0.35rem'}}>
							<div className="no-score-reason-card">
								<div className="no-score-reason-title">No Score</div>
								<div className="no-score-reason-message">{AppMessages.noOrganizationScore}</div>
							</div>
						</div>
					</div>
				</div>
			)
		}

		return (
			<div className="row" >
				<div className="col-12">
					<div className="card" style={{padding: '0.35rem'}}>
						<div className="no-score-reason-card">
							<div className="no-score-reason-title">Average Score</div>
							<div className="average-score-value">
								<span>{scorecard?.avgCurrentScore ?? ""}</span>
								{trendClass && <i className={trendClass} />}
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<Fragment>
			{ getDriversResponses.loading && <LoadingSpinner title="drivers"/>}
			<div className="container app-page-text" style={{marginTop:0}}>
				{renderPeriod(currentOrganization)}	
				<div className="row">
					<div className="col-12 align-items-center" >
						<div className="card app-group-selector-card" style={{marginBottom: "0.35rem"}}>
							<div className="card-body align-items-center" style={{padding: "0.15rem", overflow: "visible"}}>
								<Dropdown
									className="w-100 position-relative"
									onToggle={(nextShow) => {
										if (!nextShow) {
											setGroupSearchText("")
										}
									}}
								>
									<Dropdown.Toggle className="btn btn-lg dropdown-toggle w-100 fw-normal app-group-selector" id="dropdownMenuButtonLG">
										{currentSelectedGroup?.name}
									</Dropdown.Toggle>
									<Dropdown.Menu
										align="start"
										className="app-group-dropdown-menu"
										renderOnMount={true}
										popperConfig={{
											strategy: "absolute",
											modifiers: [
												{
													name: "flip",
													enabled: false,
												},
												{
													name: "preventOverflow",
													enabled: false,
												},
												{
													name: "offset",
													options: {
														offset: [0, 0],
													},
												},
											],
										}}
									>
										<input
											type="search"
											className="form-control form-control-sm app-group-search"
											placeholder="Search groups..."
											value={groupSearchText}
											onChange={(event) => {
												setGroupSearchText(event.target.value)
											}}
											onClick={(event) => {
												event.stopPropagation()
											}}
											onKeyDown={(event) => {
												event.stopPropagation()
											}}
											aria-label="Search groups"
										/>
										{
											map(filteredGroups, (group, index) => {
												const isSelected = group.groupID === currentSelectedGroup?.groupID
												return (
													<Dropdown.Item
														key={`group-key-154-${index}`}
														active={isSelected}
														onClick={event => { event.preventDefault(); setCurrentSelectedGroup(group); }}
													>
														{group.name}
													</Dropdown.Item>
												)
											})
										}
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
					</div>
				</div>
				<ManagerTopStreaksPanel topStreaks={topStreaks} />
				{ renderGroupScore() }
				{(hasGroupScore || drivers.length > 0) && (
					<div className="row app-page-text">
						<div className="col-12">
							<div className="card" style={{marginBottom: "0.35rem !important"}}>
								<div className="card-body align-items-center" style={{padding: "0.35rem"}}>
									<div className="row g-0 p-0">
										<NoContentWrapper items={drivers} message={hasData ? AppMessages.noOffendingDrivers : ""} showIcon={hasData ? true : false}>
											{ map(drivers,(d,i) => {
												return <DriverCard key={`driver-553-${i}`} isFirst={true} isSummary={true} user={ d } onClick={ e => navigateToDetails(e, d)}/>
											})}
										</NoContentWrapper>

									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</Fragment>
	)
}

export default ManagerSummary;